import React, { useState, useEffect } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import Button from '../components/Button';
import { PayrollFrequency, NameDisplayFormat } from '../../shared/types.js';
import { toDateInputString, formatEmployeeName } from '../utils.js';

interface OrangeHRMEmployee {
    emp_number: number;
    emp_firstname: string;
    emp_lastname: string;
}

const SetupWizardScreen: React.FC = () => {
    const { completeSetup } = useTimeClock();
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [allEmployees, setAllEmployees] = useState<OrangeHRMEmployee[]>([]);
    const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
    const [formData, setFormData] = useState({
        companyName: '',
        companyAddress: '',
        adminEmpNumber: '',
        adminPin: '',
        payrollFrequency: PayrollFrequency.BIWEEKLY,
        payrollStartDate: toDateInputString(new Date()),
    });
    useEffect(() => {
        if (step === 3 && allEmployees.length === 0) {
            const fetchAllEmployees = async () => {
                setIsLoadingEmployees(true);
                try {
                    const res = await fetch('/api/orangehrm/employees/all');
                    if (!res.ok) throw new Error('Could not fetch employees from OrangeHRM database.');
                    const data = await res.json();
                    setAllEmployees(data);
                    if (data.length > 0) {
                        setFormData(prev => ({ ...prev, adminEmpNumber: data[0].emp_number.toString() }));
                    }
                } catch (e: any) {
                    setError(e.message);
                } finally {
                    setIsLoadingEmployees(false);
                }
            };
            fetchAllEmployees();
        }
    }, [step, allEmployees.length]);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setError('');
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    const handleNext = () => {
        if (step === 2 && !formData.companyName) {
            setError('Company Name is required.');
            return;
        }
        if (step === 3) {
            if (!formData.adminEmpNumber) {
                setError('An admin employee must be selected.');
                return;
            }
            if (formData.adminPin.length < 4) {
                setError('PIN must be at least 4 digits long.');
                return;
            }
        }
        setStep(prev => prev + 1);
    };
    const handleBack = () => {
        setError('');
        setStep(prev => prev - 1);
    };
    const handleSubmit = async () => {
        if (!formData.payrollStartDate) {
            setError('Pay Period Start Date is required.');
            return;
        }
        setIsSubmitting(true);
        setError('');
        try {
            await completeSetup(formData);
        } catch (err: any) {
            setError(err.message || 'An unexpected error occurred.');
            setIsSubmitting(false);
        }
    };
    const inputClass = "block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-gray-900 dark:text-white focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";
    const labelClass = "block text-sm font-medium text-gray-600 dark:text-gray-300";
    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <>
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Welcome to KioskTimeClock</h1>
                        <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">Let's get your system configured. This will only take a minute.</p>
                        <img className="mt-8" src="/favicon.svg" />
                    </>
                );
            case 2:
                return (
                    <>
                        <h2 className="text-3xl font-bold">Company Details</h2>
                        <p className="mt-2 text-gray-500 dark:text-gray-400">This information will be displayed on reports.</p>
                        <div className="mt-8 space-y-4 w-full">
                             <div>
                                <label htmlFor="companyName" className={labelClass}>Company Name</label>
                                <input type="text" id="companyName" name="companyName" value={formData.companyName} onChange={handleChange} required className={inputClass} autoFocus />
                            </div>
                            <div>
                                <label htmlFor="companyAddress" className={labelClass}>Company Address (Optional)</label>
                                <textarea id="companyAddress" name="companyAddress" value={formData.companyAddress} onChange={handleChange} rows={3} className={inputClass} />
                            </div>
                        </div>
                    </>
                );
            case 3:
                return (
                    <>
                        <h2 className="text-3xl font-bold">Create Your Admin Account</h2>
                        <p className="mt-2 text-gray-500 dark:text-gray-400">Select an existing employee from OrangeHRM to be the first administrator.</p>
                        <div className="mt-8 space-y-4 w-full">
                            <div>
                                <label htmlFor="adminEmpNumber" className={labelClass}>Select Employee</label>
                                {isLoadingEmployees ? <p>Loading employees...</p> : (
                                    <select id="adminEmpNumber" name="adminEmpNumber" value={formData.adminEmpNumber} onChange={handleChange} required className={inputClass}>
                                        {allEmployees.map(emp => (
                                            <option key={emp.emp_number} value={emp.emp_number}>
                                                {formatEmployeeName({ firstName: emp.emp_firstname, lastName: emp.emp_lastname }, NameDisplayFormat.LAST_FIRST)}
                                            </option>
                                        ))}
                                        {allEmployees.length === 0 && <option disabled>No employees found in OrangeHRM</option>}
                                    </select>
                                )}
                            </div>
                            <div>
                                <label htmlFor="adminPin" className={labelClass}>Admin PIN (4+ digits)</label>
                                <input type="password" id="adminPin" name="adminPin" value={formData.adminPin} onChange={handleChange} required minLength={4} className={`${inputClass} font-mono tracking-widest`} />
                            </div>
                        </div>
                    </>
                );
            case 4:
                return (
                    <>
                        <h2 className="text-3xl font-bold">Payroll Configuration</h2>
                        <p className="mt-2 text-gray-500 dark:text-gray-400">Set the start date and frequency for your payroll periods.</p>
                         <div className="mt-8 space-y-4 w-full">
                             <div>
                                <label htmlFor="payrollFrequency" className={labelClass}>Payroll Frequency</label>
                                <select id="payrollFrequency" name="payrollFrequency" value={formData.payrollFrequency} onChange={handleChange} className={inputClass}>
                                    {Object.values(PayrollFrequency).map(freq => (
                                        <option key={freq} value={freq}>{freq}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="payrollStartDate" className={labelClass}>Pay Period Start Date</label>
                                <input type="date" id="payrollStartDate" name="payrollStartDate" value={formData.payrollStartDate} onChange={handleChange} required className={inputClass} />
                            </div>
                         </div>
                    </>
                );
            default:
                return null;
        }
    };
    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12 text-center flex flex-col items-center">
                <div className="w-full">
                    {renderStep()}
                </div>
                {error && <p className="mt-6 text-red-500 bg-red-100 dark:bg-red-900/50 p-3 rounded-md w-full">{error}</p>}
                <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 w-full flex justify-between items-center">
                    <div>
                        {step > 1 && (
                            <Button variant="secondary" onClick={handleBack} disabled={isSubmitting}>Back</Button>
                        )}
                    </div>
                    <div>
                        {step < 4 && <Button onClick={handleNext}>Next</Button>}
                        {step === 4 && <Button onClick={handleSubmit} disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Finish Setup'}</Button>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SetupWizardScreen;